import { useEffect, useRef, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";

const IOS_HINT_DISMISSED = "aegis_ios_hint_dismissed_v1";
const A2HS_DISMISSED = "aegis_a2hs_dismissed_v1";

function isStandalone() {
  return (
    (typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches) ||
    (typeof navigator !== "undefined" && navigator.standalone === true)
  );
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const iPadOS = navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
  const isSafari = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);
  return (iOSDevice || iPadOS) && isSafari;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || isStandalone()) return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!localStorage.getItem(A2HS_DISMISSED)) setVisible(true);
    };
    const onInstalled = () => {
      setDeferred(null); setVisible(false); setIosHint(false);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires beforeinstallprompt — show hint after a short delay
    if (isIos() && !localStorage.getItem(IOS_HINT_DISMISSED)) {
      timer.current = setTimeout(() => setIosHint(true), 4000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice.catch(() => null);
    if (choice?.outcome !== "accepted") {
      localStorage.setItem(A2HS_DISMISSED, "1");
    }
    setDeferred(null); setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem(A2HS_DISMISSED, "1");
    setVisible(false);
  };

  const dismissIos = () => {
    localStorage.setItem(IOS_HINT_DISMISSED, "1");
    setIosHint(false);
  };

  return (
    <>
      {visible && deferred && (
        <div
          data-testid="install-prompt"
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[360px] z-40 glass rounded-2xl p-4 shadow-xl"
        >
          <div className="flex items-start gap-3">
            <img src="/icon-192.png" alt="" className="w-10 h-10 rounded-lg" />
            <div className="flex-1 min-w-0">
              <div className="font-display text-base font-medium leading-tight">Install Aegis</div>
              <div className="text-xs text-muted-foreground mt-0.5">Add to your home screen for one-tap access.</div>
            </div>
            <button data-testid="install-dismiss" onClick={dismiss} className="text-muted-foreground hover:text-black -mt-1 -mr-1 p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
          <button
            data-testid="install-btn"
            onClick={install}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-black text-white text-sm font-bold tracking-wide py-2.5 hover:opacity-90"
          >
            <Download className="w-4 h-4" /> Install app
          </button>
        </div>
      )}

      {iosHint && (
        <div
          data-testid="ios-hint"
          className="fixed bottom-4 left-4 right-4 z-40 glass rounded-2xl p-4 shadow-xl"
        >
          <div className="flex items-start gap-3">
            <img src="/icon-192.png" alt="" className="w-10 h-10 rounded-lg" />
            <div className="flex-1 min-w-0">
              <div className="font-display text-base font-medium leading-tight">Install Aegis on iPhone</div>
              <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Tap <Share className="inline w-3.5 h-3.5 align-[-2px]" strokeWidth={2} /> <b>Share</b>, then{" "}
                <b>Add to Home Screen</b> <Plus className="inline w-3.5 h-3.5 align-[-2px]" strokeWidth={2} />.
              </div>
            </div>
            <button data-testid="ios-hint-dismiss" onClick={dismissIos} className="text-muted-foreground hover:text-black -mt-1 -mr-1 p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
